import React from 'react';
import { usePermission } from '../../shared/hooks/usePermission';
import type { PermissionKey } from '../../shared/permissions';
import { AccessDenied } from './AccessDenied';

export interface RequirePermissionProps {
  /** Permission key the user must hold to see `children`. */
  perm: PermissionKey;
  /** Friendly name passed to <AccessDenied/> when permission is denied. */
  sectionName?: string;
  /** Override route the AccessDenied CTA navigates to. */
  fallbackRoute?: string;
  /** Custom CTA label for the fallback. */
  fallbackLabel?: string;
  /** Custom fallback component (overrides AccessDenied). */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * UI gate around a route element. The check mirrors the server-side
 * `assertCan(perm)` middleware — when the user lacks the permission we render
 * <AccessDenied/> instead of the protected content. The server still returns
 * 403 for every gated endpoint independent of this guard.
 *
 * Loading state: returns `null` so the page area stays blank while AuthContext
 * is hydrating. The parent route wrappers (RequireAuth, RequireOrg) already
 * own the spinner UX, so emitting our own spinner here would compound it.
 */
export const RequirePermission: React.FC<RequirePermissionProps> = ({
  perm,
  sectionName,
  fallbackRoute,
  fallbackLabel,
  fallback,
  children,
}) => {
  const { allowed } = usePermission(perm);
  if (allowed) return <>{children}</>;
  if (fallback) return <>{fallback}</>;
  return (
    <AccessDenied sectionName={sectionName} backTo={fallbackRoute} backLabel={fallbackLabel} />
  );
};
